<div class="c-modal c-modal--alt" [class.is-visible]="isOpen()">
    <div class="c-modal__overlay" (click)="onClose()"></div>
    <div class="c-modal__holder pda-panel">

        <!-- Header -->
        <div class="c-modal__header">
            <div class="pda-panel__header-title">
                <h2 class="c-modal__title">
                    {{ 'pda.title' | translate }}
                    @if (portCallTitle()) {
                        <span class="pda-panel__port-name"> — {{ portCallTitle() }}</span>
                    }
                </h2>
            </div>
            <button class="c-modal__close" type="button" (click)="onClose()">
                <i class="fa-solid fa-xmark"></i>
            </button>
        </div>

        <!-- Body -->
        <div class="c-modal__body pda-panel__body">

            <!-- Loading -->
            <core-loader />

            <!-- Error -->
            @if (error()) {
                <div class="c-alert context:error">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    {{ error() }}
                </div>
            }

            <!-- Vessel info strip -->
            @if (pda()?.vessel || pda()?.stayDays) {
                <div class="pda-vessel-strip">
                    <!-- Col 1: Vessel -->
                    <div class="pda-vessel-strip__col">
                        <div class="pda-vessel-strip__col-title">
                            <i class="fa-solid fa-ship"></i>
                            {{ 'pda.vessel' | translate }}
                        </div>
                        <div class="pda-vessel-strip__col-value">{{ pda()?.vessel?.name ?? '—' }}</div>
                    </div>
                    <!-- Col 2: Stay -->
                    <div class="pda-vessel-strip__col">
                        <div class="pda-vessel-strip__col-title">
                            <i class="fa-regular fa-clock"></i>
                            {{ 'pda.stay' | translate }}
                        </div>
                        <div class="pda-vessel-strip__col-value">
                            {{ pda()?.stayHours ?? '—' }}h
                            @if (pda()?.stayDays) {
                                <span class="pda-vessel-strip__col-value-sub">· {{ pda()!.stayDays }} {{ 'pda.days' | translate }}</span>
                            }
                        </div>
                    </div>
                    <!-- Col 3: Cargo -->
                    <div class="pda-vessel-strip__col">
                        <div class="pda-vessel-strip__col-title">
                            <i class="fa-solid fa-boxes-stacked"></i>
                            {{ 'pda.bookings' | translate }}
                        </div>
                        <div class="pda-vessel-strip__col-value">
                            {{ cargoOperationSummary() || '—' }} {{ 'pda.units' | translate }}
                        </div>
                    </div>
                </div>
            }

            <!-- Stale warning -->
            @if (hasOutdatedInputs()) {
                <div class="c-alert context:warning pda-stale-banner">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    <div>
                        <strong>{{ 'pda.staleTitle' | translate }}</strong>
                        <p>{{ 'pda.staleMessage' | translate }}</p>
                        @if ((pda()?.staleReasons ?? []).length > 0) {
                            <ul class="pda-stale-banner__reasons">
                                @for (reason of pda()!.staleReasons; track reason) {
                                    <li>{{ ('pda.staleReason.' + reason) | translate }}</li>
                                }
                            </ul>
                        }
                    </div>
                </div>
            }

            <!-- Manual overrides notice -->
            @if (pda()?.hasManualOverrides) {
                <div class="c-alert context:info pda-override-notice">
                    <i class="fa-solid fa-pen-to-square"></i>
                    {{ 'pda.manualOverridesNotice' | translate }}
                </div>
            }

            <!-- No PDA empty state -->
            @if (notFound() && !error()) {
                <div class="c-placeholder c-placeholder--centered">
                    <p class="c-placeholder__icon"><i class="fa-regular fa-file-lines"></i></p>
                    <p class="c-placeholder__heading">{{ 'pda.noActivePda' | translate }}</p>
                    <p class="c-placeholder__hint">{{ 'pda.noActivePdaHint' | translate }}</p>
                    <button class="c-btn c-btn--primary" type="button" (click)="onCreatePda()">
                        <i class="fa-solid fa-plus"></i>
                        {{ 'pda.createPda' | translate }}
                    </button>
                </div>
            }

            <!-- PDA content -->
            @if (pda(); as pda) {
                <!-- PDA header bar -->
                <div class="pda-section-header">
                    <div class="pda-section-header__left">
                        <i class="fa-regular fa-file-lines"></i>
                        <span>{{ 'pda.pdaTitle' | translate }}</span>
                        <span class="c-tag context:{{ pdaStatusContext(pda.status) }}">
                            {{ ('pda.status.' + pda.status) | translate }}
                        </span>
                        @if (pda.previousTotalUsd) {
                            <span class="pda-section-header__prev-total">
                                {{ formatAmount(pda.previousTotalUsd) }}
                            </span>
                        }
                        @if (pda.sourceTariffId) {
                            <span class="pda-section-header__tariff" [title]="'pda.tariffSourceTooltip' | translate">
                                {{ 'pda.tariffSource' | translate }}: #{{ pda.sourceTariffId }}
                            </span>
                        }
                        @if (pda.calculatedAt) {
                            <span class="pda-section-header__calculated-at">
                                {{ 'pda.calculatedAt' | translate }}: {{ pda.calculatedAt | date:'dd/MM/yyyy HH:mm' }}
                            </span>
                        }
                    </div>
                    <button
                        class="c-btn c-btn--outline c-btn--sm"
                        type="button"
                        (click)="openRecalculateConfirm()"
                        [disabled]="isApproved()">
                        <i class="fa-solid fa-rotate-right"></i>
                        {{ 'pda.recalculate' | translate }}
                    </button>
                </div>

                <!-- Category groups: Port / Stevedoring / Agency, each with its own subtotal and manual-add control -->
                @for (group of lineGroups(); track group.category) {
                    <div class="pda-category-group">
                        <div class="pda-category-group__header">
                            <span class="pda-category-group__title">
                                {{ ('portTariffLines.options.expenseCategory.' + group.category) | translate }}
                            </span>
                        </div>

                        <div class="pda-category-group__body">
                        @if (group.lines.length > 0) {
                            <div class="pda-table-scroll">
                                <table class="pda-lines-table">
                                    <thead>
                                        <tr>
                                            <th>{{ 'pda.col.concept' | translate }}</th>
                                            <th class="pda-lines-table__direction-col"></th>
                                            <th>{{ 'pda.col.formula' | translate }}</th>
                                            <th class="pda-lines-table__amount-col">{{ 'pda.col.amount' | translate }}</th>
                                            <th>{{ 'pda.col.status' | translate }}</th>
                                            <th class="pda-lines-table__actions-col"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        @for (line of group.lines; track line.id) {
                                            @if (editingLineId() === line.id) {
                                                <tr class="pda-lines-table__edit-row">
                                                    <td colspan="6">
                                                        <div class="pda-inline-edit">
                                                            <div class="pda-inline-edit__concept">{{ line.concept }}</div>
                                                            <div class="pda-inline-edit__fields">
                                                                <div class="pda-inline-edit__field">
                                                                    <label class="pda-inline-edit__label">{{ 'pda.override.amount' | translate }}</label>
                                                                    <input
                                                                        type="number"
                                                                        step="0.01"
                                                                        min="0"
                                                                        [value]="overrideDraft().manualAmountUsd"
                                                                        (input)="updateOverrideDraft('manualAmountUsd', $any($event.target).value)" />
                                                                </div>
                                                                <div class="pda-inline-edit__field">
                                                                    <label class="pda-inline-edit__label">{{ 'pda.override.reason' | translate }}</label>
                                                                    <input
                                                                        type="text"
                                                                        [value]="overrideDraft().overrideReason"
                                                                        (input)="updateOverrideDraft('overrideReason', $any($event.target).value)"
                                                                        [placeholder]="'pda.override.reasonPlaceholder' | translate" />
                                                                </div>
                                                            </div>
                                                            <div class="pda-inline-edit__actions">
                                                                <button class="c-btn c-btn--sm c-btn--primary" type="button" (click)="onSaveOverride(line.id)">
                                                                    {{ 'pda.override.save' | translate }}
                                                                </button>
                                                                <button class="c-btn c-btn--sm c-btn--outline" type="button" (click)="onCancelEdit()">
                                                                    {{ 'pda.override.cancel' | translate }}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            } @else {
                                                <tr [class.pda-lines-table__row--overridden]="line.isAmountOverridden" [class.pda-lines-table__row--missing]="line.status === PdaLineStatus.MISSING_DATA">
                                                    <td class="pda-lines-table__concept">
                                                        <div class="pda-lines-table__title">
                                                            @if (lineHasExpandableDetails(line)) {
                                                                <button
                                                                    type="button"
                                                                    class="pda-ot-toggle"
                                                                    (click)="toggleOvertimeBreakdown(line.id)"
                                                                    [title]="'pda.overtime.toggle' | translate">
                                                                    <i class="fa-solid" [class.fa-chevron-right]="!isOvertimeExpanded(line.id)" [class.fa-chevron-down]="isOvertimeExpanded(line.id)"></i>
                                                                </button>
                                                            }
                                                            <span>{{ line.concept }}</span>
                                                            @if (line.cargoTypeName) {
                                                                <span class="c-tag pda-lines-table__category-tag">{{ line.cargoTypeName }}</span>
                                                            }
                                                        </div>
                                                        @if (lineHasExpandableDetails(line)) {
                                                            <div class="pda-ot-badges">
                                                                @if (lineOvertimeLevels(line).length) {
                                                                    @for (level of lineOvertimeLevels(line); track level) {
                                                                        <span class="c-tag pda-ot-badge" [class.context:warning]="level === PortOvertimeLevel.OT1" [class.context:error]="level === PortOvertimeLevel.OT2">
                                                                            <i class="fa-solid fa-clock"></i>
                                                                            {{ ('pda.overtime.level.' + level) | translate }}
                                                                            <span class="c-tooltip c-tooltip--top">{{ 'pda.overtime.badgeTooltip' | translate }}</span>
                                                                        </span>
                                                                    }
                                                                } @else if (lineHasOvertimeBreakdown(line)) {
                                                                    <span class="c-tag pda-ot-badge context:warning">
                                                                        <i class="fa-solid fa-clock"></i>
                                                                        {{ 'pda.overtime.badge' | translate }}
                                                                        <span class="c-tooltip c-tooltip--top">{{ 'pda.overtime.badgeTooltip' | translate }}</span>
                                                                    </span>
                                                                }
                                                                @if (lineHasMissingOvertimeRate(line)) {
                                                                    <span class="c-tag context:warning pda-ot-badge">
                                                                        <i class="fa-solid fa-plug-circle-exclamation"></i>
                                                                        {{ 'pda.overtime.rateMissingBadge' | translate }}
                                                                        <span class="c-tooltip c-tooltip--top">{{ 'pda.overtime.rateMissingTooltip' | translate }}</span>
                                                                    </span>
                                                                }
                                                                @if (lineHasFallbackApplied(line)) {
                                                                    <span class="c-tag context:info pda-ot-badge">
                                                                        <i class="fa-solid fa-arrow-turn-down"></i>
                                                                        {{ 'pda.overtime.fallbackBadge' | translate }}
                                                                    </span>
                                                                }
                                                                @if (line.cargoMatchStatus === CARGO_MATCH_NOT_FOUND) {
                                                                    <span class="c-tag context:default pda-ot-badge">
                                                                        {{ 'pda.calc.notFoundBadge' | translate }}
                                                                        <span class="c-tooltip c-tooltip--top">{{ 'pda.calc.notFoundNote' | translate }}</span>
                                                                    </span>
                                                                }
                                                            </div>
                                                        }
                                                    </td>
                                                    <td class="pda-lines-table__direction-col">
                                                        <div class="pda-lines-table__direction-stack">
                                                            @for (direction of operationDirectionIcons(line.operationDirection); track direction.icon) {
                                                                <i class="{{ direction.icon }} pda-lines-table__direction-icon" [title]="direction.labelKey | translate"></i>
                                                            }
                                                        </div>
                                                    </td>
                                                    <td class="pda-lines-table__formula">
                                                        @if (line.formula) {
                                                            <em>{{ line.formula }}</em>
                                                        }
                                                    </td>
                                                    <td class="pda-lines-table__amount">
                                                        @if (line.isAmountOverridden) {
                                                            <span class="pda-amount--overridden">{{ formatAmount(line.effectiveAmountUsd) }}</span>
                                                            <span class="pda-amount--calculated-strike">{{ formatAmount(line.calculatedAmountUsd) }}</span>
                                                        } @else {
                                                            <span>{{ formatAmount(line.effectiveAmountUsd) }}</span>
                                                        }
                                                    </td>
                                                    <td>
                                                        @if (line.isAmountOverridden) {
                                                            <span class="c-tag context:warning">
                                                                {{ 'pda.lineStatus.overridden' | translate }}
                                                                @if (line.overrideReason) {
                                                                    <span class="c-tooltip c-tooltip--top">{{ line.overrideReason }}</span>
                                                                }
                                                            </span>
                                                        } @else {
                                                            <span class="c-tag context:{{ lineStatusContext(line.status) }}">
                                                                {{ ('pda.lineStatus.' + line.status) | translate }}
                                                            </span>
                                                        }
                                                    </td>
                                                    <td class="pda-lines-table__actions">
                                                        @if (!isApproved()) {
                                                            <div class="pda-line-actions">
                                                                @if (line.isAmountOverridden) {
                                                                    <button class="c-btn c-btn--stroke" type="button" (click)="onResetLine(line.id)" [title]="'pda.override.reset' | translate">
                                                                        <i class="fa-solid fa-rotate-left"></i>
                                                                    </button>
                                                                }
                                                                <button class="c-btn c-btn--stroke" type="button" (click)="onEditLine(line)" [title]="'pda.override.edit' | translate">
                                                                    <i class="fa-solid fa-pen"></i>
                                                                </button>
                                                            </div>
                                                        }
                                                    </td>
                                                </tr>
                                                @if (lineHasExpandableDetails(line) && isOvertimeExpanded(line.id)) {
                                                    <tr class="pda-ot-breakdown-row">
                                                        <td colspan="6">
                                                            <div class="pda-ot-breakdown">
                                                                @if (lineHasCalculationDetails(line)) {
                                                                    <div class="pda-calc-details">
                                                                        <p class="pda-calc-details__title">{{ 'pda.calc.title' | translate }}</p>
                                                                        <div class="pda-calc-details__grid">
                                                                            @if (line.cargoMeasureType) {
                                                                                <div class="pda-calc-details__row">
                                                                                    <span class="pda-calc-details__label">{{ 'pda.calc.measureType' | translate }}</span>
                                                                                    <span class="pda-calc-details__value">{{ cargoMeasureTypeLabel(line.cargoMeasureType) }}</span>
                                                                                </div>
                                                                                <div class="pda-calc-details__row">
                                                                                    <span class="pda-calc-details__label">{{ 'pda.calc.measureValue' | translate }}</span>
                                                                                    <span class="pda-calc-details__value">{{ cargoMeasureValueDisplay(line) }}</span>
                                                                                </div>
                                                                            }
                                                                            @if (line.cargoTypeName) {
                                                                                <div class="pda-calc-details__row">
                                                                                    <span class="pda-calc-details__label">{{ 'pda.calc.cargoType' | translate }}</span>
                                                                                    <span class="pda-calc-details__value">{{ line.cargoTypeName }}</span>
                                                                                </div>
                                                                            }
                                                                        </div>
                                                                        @if (line.cargoMatchStatus === CARGO_MATCH_NOT_FOUND) {
                                                                            <div class="pda-calc-details__note pda-calc-details__note--info">
                                                                                <i class="fa-solid fa-circle-info"></i>
                                                                                {{ 'pda.calc.notFoundNote' | translate }}
                                                                            </div>
                                                                        }
                                                                        @if (line.status === PdaLineStatus.MISSING_DATA) {
                                                                            <div class="pda-calc-details__note pda-calc-details__note--warning">
                                                                                <i class="fa-solid fa-triangle-exclamation"></i>
                                                                                {{ 'pda.calc.missingDataNote' | translate }}
                                                                            </div>
                                                                        }
                                                                    </div>
                                                                }
                                                                @if (lineHasOvertimeBreakdown(line)) {
                                                                    @if (line.isAmountOverridden) {
                                                                        <div class="pda-ot-breakdown__override-note">
                                                                            <i class="fa-solid fa-circle-info"></i>
                                                                            {{ 'pda.overtime.overridePriorityNote' | translate }}
                                                                        </div>
                                                                    }
                                                                    @if (line.overtimeReason) {
                                                                        <div class="pda-ot-breakdown__reason">{{ line.overtimeReason }}</div>
                                                                    }
                                                                    <table class="pda-ot-breakdown__table">
                                                                        <thead>
                                                                            <tr>
                                                                                <th>{{ 'pda.overtime.col.level' | translate }}</th>
                                                                                <th>{{ 'pda.overtime.col.hours' | translate }}</th>
                                                                                <th>{{ 'pda.overtime.col.rate' | translate }}</th>
                                                                                <th class="pda-lines-table__amount-col">{{ 'pda.overtime.col.amount' | translate }}</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            @for (entry of lineOvertimeEntries(line); track entry.level) {
                                                                                <tr [class.pda-ot-breakdown__row--missing]="!entry.rateApplied">
                                                                                    <td>{{ ('pda.overtime.level.' + entry.level) | translate }}</td>
                                                                                    <td>{{ formatHours(entry.hours) }}</td>
                                                                                    <td>
                                                                                        @if (entry.rateApplied) {
                                                                                            {{ entry.rateValue ?? '—' }}
                                                                                            @if (entry.fallbackCode) {
                                                                                                <span class="pda-ot-breakdown__fallback-note" [title]="('pda.overtime.fallbackTooltip' | translate: { code: entry.fallbackCode })">
                                                                                                    <i class="fa-solid fa-arrow-turn-down"></i>
                                                                                                    {{ 'pda.overtime.fallbackBadge' | translate }}
                                                                                                </span>
                                                                                            }
                                                                                        } @else {
                                                                                            <span class="pda-ot-breakdown__rate-missing">
                                                                                                <i class="fa-solid fa-triangle-exclamation"></i>
                                                                                                {{ 'pda.overtime.rateNotConfigured' | translate }}
                                                                                            </span>
                                                                                        }
                                                                                    </td>
                                                                                    <td class="pda-lines-table__amount-col">{{ formatAmount(entry.amountUsd) }}</td>
                                                                                </tr>
                                                                            }
                                                                        </tbody>
                                                                    </table>
                                                                }
                                                            </div>
                                                        </td>
                                                    </tr>
                                                }
                                            }
                                        }
                                    </tbody>
                                </table>
                            </div><!-- .pda-table-scroll -->
                        } @else {
                            <p class="pda-category-group__empty">{{ 'pda.category.emptyHint' | translate }}</p>
                        }

                        <!-- Subtotal + manual-add button/form, below the table -->
                        @if (!isApproved()) {
                            @if (addingManualItemFor() === group.category) {
                                <div class="pda-inline-edit pda-category-group__manual-form">
                                    <div class="pda-inline-edit__concept">{{ 'pda.manualItem.title' | translate }}</div>
                                    <div class="pda-inline-edit__fields pda-inline-edit__fields--2col">
                                        <div class="pda-inline-edit__field">
                                            <label class="pda-inline-edit__label">{{ 'pda.manualItem.concept' | translate }}</label>
                                            <input type="text"
                                                [value]="manualItemDraft().concept"
                                                (input)="updateManualItemDraft('concept', $any($event.target).value)"
                                                [placeholder]="'pda.manualItem.conceptPlaceholder' | translate" />
                                        </div>
                                        <div class="pda-inline-edit__field">
                                            <label class="pda-inline-edit__label">{{ 'pda.manualItem.rateValue' | translate }}</label>
                                            <input type="number" step="0.0001" min="0"
                                                [value]="manualItemDraft().rateValue"
                                                (input)="updateManualItemDraft('rateValue', $any($event.target).value)" />
                                        </div>
                                    </div>
                                    <div class="pda-inline-edit__actions">
                                        <button class="c-btn c-btn--sm" type="button" (click)="onSaveManualItem()">
                                            {{ 'pda.override.save' | translate }}
                                        </button>
                                        <button class="c-btn c-btn--stroke c-btn--sm" type="button" (click)="onCancelManualItem()">
                                            {{ 'pda.override.cancel' | translate }}
                                        </button>
                                    </div>
                                </div>
                            } @else {
                                <button class="c-btn c-btn--stroke c-btn--sm pda-category-group__manual-btn" type="button" (click)="onAddManualItem(group.category)">
                                    <i class="fa-solid fa-plus"></i>
                                    {{ 'pda.manualItem.add' | translate }}
                                </button>
                            }
                        }

                        <div class="pda-category-group__subtotal-row">
                            <span class="pda-category-group__subtotal-label">{{ 'pda.category.subtotal' | translate }}</span>
                            <span class="pda-category-group__subtotal">{{ formatAmount(group.subtotal) }}</span>
                        </div>
                        </div><!-- .pda-category-group__body -->
                    </div><!-- .pda-category-group -->
                }

                <!-- Total row -->
                <div class="pda-total-row">
                    <span class="pda-total-row__label">{{ 'pda.totalEstimated' | translate }}</span>
                    <span class="pda-total-row__amount">{{ formatAmount(pda.totalUsd) }}</span>
                </div>
            }

        </div><!-- .c-modal__body -->

        <!-- Footer -->
        <div class="c-modal__bottom">
            <button class="c-btn c-btn--outline" type="button" (click)="onClose()">
                {{ 'pda.close' | translate }}
            </button>
            @if (pda()) {
                <button
                    class="c-btn c-btn--primary"
                    type="button"
                    (click)="onApprove()"
                    [disabled]="isApproved()">
                    <i class="fa-solid fa-check"></i>
                    {{ isApproved() ? ('pda.approved' | translate) : ('pda.approvePda' | translate) }}
                </button>
            }
        </div>

    </div><!-- .c-modal__holder -->
</div><!-- .c-modal -->

<!-- Recalculate confirmation -->
<div class="c-popup" [class.is-visible]="showRecalculateConfirm()">
    <div class="c-popup__overlay" (click)="closeRecalculateConfirm()"></div>
    <div class="c-popup__holder">
        <p class="c-popup__title u-heading u-fz--600">
            {{ 'pda.recalculateConfirm.title' | translate }}
        </p>
        <p class="c-popup__text u-text">
            {{ 'pda.recalculateConfirm.message' | translate }}
        </p>

        <label class="c-entry-toggle">
            <input
                type="checkbox"
                [checked]="syncTariffOnRecalculate()"
                (change)="syncTariffOnRecalculate.set($any($event.target).checked)">
            <span class="c-entry-toggle__toggle"></span>
            <span class="c-entry-toggle__text">{{ 'pda.recalculateConfirm.syncTariff' | translate }}</span>
        </label>

        <div class="c-popup__btns u-flex u-push-t--xl">
            <button class="c-btn c-btn--outline" type="button" (click)="closeRecalculateConfirm()">
                {{ 'dialog.cancel' | translate }}
            </button>
            <button class="c-btn c-btn--primary" type="button" (click)="confirmRecalculate()">
                {{ 'dialog.confirm' | translate }}
            </button>
        </div>
    </div>
</div>
